import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Calendar, Users, Download, Loader2, Sparkles, ClipboardCheck, ChevronDown, CalendarX2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ViewerUnlockCard } from "@/components/ViewerUnlock";

// Helpers
const pad = (n) => String(n).padStart(2, "0");
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthStart = (d = new Date()) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
const monthEnd = (d = new Date()) => {
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(lastDay)}`;
};
const formatDateIt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const monthLabelIt = (iso) => {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  const names = [
    "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
    "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
  ];
  return `${names[Number(m) - 1]} ${y}`;
};

// Iter 31k: calcolo Pasqua (algoritmo di Butcher) per identificare i festivi mobili
function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const mm = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * mm + 114) / 31);
  const day = ((h + L - 7 * mm + 114) % 31) + 1;
  return { month, day };
}
function isWeekendOrHolidayIT(iso) {
  if (!iso) return false;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const wd = dt.getDay(); // 0=dom, 6=sab
  if (wd === 0 || wd === 6) return true;
  const fixed = new Set([
    "1-1", "1-6", "4-25", "5-1", "6-2", "8-15",
    "11-1", "12-8", "12-25", "12-26",
  ]);
  if (fixed.has(`${m}-${d}`)) return true;
  const { month: em, day: ed } = easterDate(y);
  const easter = new Date(y, em - 1, ed);
  const pasquetta = new Date(y, em - 1, ed + 1);
  return (
    (dt.getMonth() === easter.getMonth() && dt.getDate() === easter.getDate()) ||
    (dt.getMonth() === pasquetta.getMonth() && dt.getDate() === pasquetta.getDate())
  );
}
function dayClass(iso, oreTot) {
  // Verde = lavorato, Rosso/blu chiaro = weekend/festivo, Grigio = non lavorato feriale
  if (oreTot && oreTot > 0) {
    return "bg-emerald-50 border-emerald-200";
  }
  if (isWeekendOrHolidayIT(iso)) {
    const [, , d] = iso.split("-").map(Number);
    const dt = new Date();
    dt.setFullYear(Number(iso.split("-")[0]), Number(iso.split("-")[1]) - 1, d);
    return dt.getDay() === 0
      ? "bg-rose-50 border-rose-200 text-rose-900"
      : "bg-sky-50 border-sky-200 text-sky-900";
  }
  return "bg-slate-50 border-slate-200 text-slate-500";
}

export default function PrintReportsPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  // Iter 50: selezione MULTIPLA di dipendenti (vuoto = tutti)
  const [selectedEmps, setSelectedEmps] = useState([]);
  // Iter 31n — Default = MESE PRECEDENTE (es. se entri il 20/06 vedi tutto maggio).
  // Esiste comunque il preset "Mese corrente" per uno switch immediato.
  const _now = new Date();
  const _prevMonth = new Date(_now.getFullYear(), _now.getMonth() - 1, 1);
  const _pad2 = (n) => String(n).padStart(2, "0");
  const _firstOfMonth = (d) =>
    `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-01`;
  const _lastOfMonth = (d) => {
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return `${last.getFullYear()}-${_pad2(last.getMonth() + 1)}-${_pad2(last.getDate())}`;
  };
  const [dateFrom, setDateFrom] = useState(_firstOfMonth(_prevMonth));
  const [dateTo, setDateTo] = useState(_lastOfMonth(_prevMonth));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Carica employees + primo report in parallelo all'init
  useEffect(() => {
    let cancel = false;
    const init = async () => {
      try {
        const [empRes, reportRes] = await Promise.all([
          api.get("/employees"),
          api.get("/rapportini/report/employees-daily", {
            params: { date_from: dateFrom, date_to: dateTo, only_vidimati: false },
          }),
        ]);
        if (cancel) return;
        setEmployees(empRes.data || []);
        setReport(reportRes.data);
      } catch (e) {
        console.warn("[print-reports] init failed:", e?.message);
      }
    };
    init();
    return () => { cancel = true; };
  }, []);

  const load = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Inserisci date inizio e fine");
      return;
    }
    if (dateFrom > dateTo) {
      toast.error("La data inizio deve essere prima della data fine");
      return;
    }
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, only_vidimati: false };
      const { data } = await api.get("/rapportini/report/employees-daily", { params });
      setReport(data);
    } catch (e) {
      toast.error("Caricamento report non riuscito");
    } finally {
      setLoading(false);
    }
  };

  // Quick range presets
  const setQuickRange = (kind) => {
    const now = new Date();
    if (kind === "thisMonth") {
      setDateFrom(monthStart(now));
      setDateTo(monthEnd(now));
    } else if (kind === "prevMonth") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setDateFrom(monthStart(prev));
      setDateTo(monthEnd(prev));
    } else if (kind === "thisYear") {
      setDateFrom(`${now.getFullYear()}-01-01`);
      setDateTo(`${now.getFullYear()}-12-31`);
    } else if (kind === "last3Months") {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
      setDateFrom(monthStart(start));
      setDateTo(monthEnd(end));
    } else if (kind === "lastWeek") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      setDateFrom(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`);
      setDateTo(`${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`);
    }
  };

  const exportCsv = () => {
    if (!shownEmps.length) return;
    const rows = [
      ["Dipendente", "Data", "Ore lavoro", "Ore caldo", "Ore notturne",
       "Ore guida", "Ore passeggero", "Totale ore", "Ore da vidimare (DV)", "Commesse", "Rapportini"],
    ];
    for (const emp of shownEmps) {
      for (const g of emp.giorni) {
        rows.push([
          emp.employee_nome,
          g.data,
          g.ore_lavoro,
          g.ore_caldo,
          g.ore_notturne,
          g.ore_guida || 0,
          g.ore_passegg || 0,
          g.ore_tot,
          g.ore_non_vidimate || 0,
          (g.commesse || []).join(";"),
          (g.rapportini || []).join(";"),
        ]);
      }
      rows.push([
        `TOTALE ${emp.employee_nome}`, `${emp.tot_giorni} gg`,
        emp.tot_ore_lavoro, emp.tot_ore_caldo, emp.tot_ore_notturne,
        emp.tot_ore_guida || 0, emp.tot_ore_passegg || 0, emp.tot_ore,
        emp.tot_ore_non_vidimate || 0, "", "",
      ]);
    }
    const csv = rows.map((r) =>
      r.map((c) => {
        const s = String(c ?? "");
        return s.includes(",") || s.includes('"') || s.includes("\n")
          ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-ore_${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sameMonth =
    dateFrom && dateTo &&
    dateFrom.slice(0, 7) === dateTo.slice(0, 7) &&
    dateFrom.endsWith("-01") &&
    monthEnd(new Date(`${dateFrom}T00:00:00`)) === dateTo;

  // Iter 50: dipendenti effettivamente mostrati (filtro multi-selezione lato client)
  const shownEmps = useMemo(() => {
    const list = report?.per_dipendente || [];
    if (selectedEmps.length === 0) return list;
    const sel = new Set(selectedEmps);
    return list.filter((e) => e.employee_id && sel.has(e.employee_id));
  }, [report, selectedEmps]);

  const toggleEmp = (id) =>
    setSelectedEmps((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const totalAll = useMemo(() => {
    return {
      ore: shownEmps.reduce((s, e) => s + (e.tot_ore || 0), 0),
      gg: shownEmps.reduce((s, e) => s + (e.tot_giorni || 0), 0),
      dip: shownEmps.length,
    };
  }, [shownEmps]);

  // Visualizzatore: stampe ore disabilitate dall'admin (Iter 51)
  if (user?.role === "viewer" && user?.can_view_stampe_ore === false) {
    return (
      <div
        className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center text-slate-500"
        data-testid="stampe-disabled-message"
      >
        Le stampe ore dipendenti non sono abilitate per questo account.
      </div>
    );
  }

  // Visualizzatore: le stampe richiedono il reinserimento della password
  if (user?.role === "viewer" && !user?.view_unlocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <ViewerUnlockCard
          title="Stampe protette"
          description="Inserisci di nuovo la tua password per accedere alle stampe e ai report."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header non stampabile */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-heading font-black tracking-tight">
            Stampe ore dip.
          </h1>
          <div
            className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-semibold text-amber-800"
            data-testid="report-only-vidimati-note"
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            Le ore di rapportini non ancora vidimati sono segnate con <b>(DV)</b> = da vidimare
          </div>
        </div>
        <div className="flex gap-2">
          {user?.can_view_controllo_ore !== false && (
            <Button asChild variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="stampe-to-controllo">
              <Link to="/controllo-ore"><CalendarX2 className="h-4 w-4 mr-2" />Controllo giornate mancanti</Link>
            </Button>
          )}
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={async () => {
                if (
                  !window.confirm(
                    "Recupera ore di nomi ambigui: scansiona tutti i rapportini e cerca di collegare le righe con nomi storpiati al dipendente più simile.\n\nProcedere?"
                  )
                )
                  return;
                try {
                  const { data } = await api.post(
                    "/admin/rapportini/resolve-all"
                  );
                  if (data.total_resolved === 0 && data.total_unresolved === 0) {
                    toast.success("Tutti i rapportini sono già collegati correttamente.");
                  } else {
                    const msg = `${data.total_resolved} righe recuperate in ${data.rapportini_touched} rapportini.${
                      data.total_unresolved
                        ? ` ${data.total_unresolved} nomi ancora ambigui (vedi console).`
                        : ""
                    }`;
                    toast.success(msg, { duration: 8000 });
                    if (data.unresolved_samples?.length) {
                      console.warn(
                        "Nomi non riconosciuti:",
                        data.unresolved_samples
                      );
                    }
                  }
                  // Reload report per vedere le ore ora visibili
                  load();
                } catch (e) {
                  toast.error("Recupero ore non riuscito");
                }
              }}
              data-testid="recupera-ore-ambigue"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Scansiona i rapportini esistenti e collega le righe con nomi ambigui ai dipendenti più simili (es. 'POLINI GIAN FRANCO' → 'Polini Gianfranco')"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Recupera ore ambigue
            </Button>
          )}
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={!shownEmps.length}
            data-testid="report-export-csv"
          >
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          <Button
            onClick={() => window.print()}
            disabled={!shownEmps.length}
            className="bg-[#0044FF] hover:bg-[#0033CC]"
            data-testid="report-print-button"
          >
            <Printer className="h-4 w-4 mr-2" /> Stampa
          </Button>
        </div>
      </div>

      {/* Filtri */}
      <Card className="p-4 md:p-5 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="text-xs font-bold">Dal</Label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
              data-testid="report-date-from"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs font-bold">Al</Label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
              data-testid="report-date-to"
            />
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs font-bold">Dipendenti</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full mt-1 h-11 justify-between font-normal"
                  data-testid="report-employee-select"
                >
                  <span className="truncate">
                    {selectedEmps.length === 0
                      ? "Tutti i dipendenti"
                      : selectedEmps.length === 1
                      ? (() => {
                          const e = employees.find((x) => x.id === selectedEmps[0]);
                          return e ? `${e.cognome} ${e.nome}` : "1 selezionato";
                        })()
                      : `${selectedEmps.length} dipendenti selezionati`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <button
                    type="button"
                    className="text-xs font-bold text-[#0044FF] hover:underline"
                    onClick={() => setSelectedEmps([])}
                    data-testid="report-employee-select-all"
                  >
                    Tutti
                  </button>
                  <span className="text-xs text-slate-500">
                    {selectedEmps.length === 0
                      ? "tutti inclusi"
                      : `${selectedEmps.length} selezionati`}
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                  {employees.map((e) => (
                    <label
                      key={e.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
                      data-testid={`report-employee-option-${e.id}`}
                    >
                      <Checkbox
                        checked={selectedEmps.includes(e.id)}
                        onCheckedChange={() => toggleEmp(e.id)}
                      />
                      <span className="truncate">{e.cognome} {e.nome}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="md:col-span-2">
            <Button
              onClick={load}
              disabled={loading}
              className="w-full h-11 bg-[#0044FF] hover:bg-[#0033CC]"
              data-testid="report-load-button"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Preset rapidi */}
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => setQuickRange("thisMonth")}
            data-testid="report-range-thismonth">
            <Calendar className="h-3.5 w-3.5 mr-1" /> Mese corrente
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange("prevMonth")}
            data-testid="report-range-prevmonth">
            Mese precedente
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange("lastWeek")}>
            Ultimi 7 giorni
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange("last3Months")}>
            Ultimi 3 mesi
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange("thisYear")}>
            Anno corrente
          </Button>
        </div>
      </Card>

      {/* AREA STAMPABILE */}
      <div className="print-area">
        {/* Intestazione stampabile (solo a video, non in stampa: ogni dipendente ha la sua) */}
        <div className="mb-6 print:hidden">
          <div className="flex items-baseline justify-between border-b-2 border-slate-800 pb-2 mb-2">
            <div>
              <div className="text-2xl font-heading font-black tracking-tight">
                Report ore dipendenti
              </div>
              <div className="text-sm text-slate-600">
                {sameMonth ? (
                  <>Periodo: <b>{monthLabelIt(dateFrom)}</b></>
                ) : (
                  <>Periodo: <b>{formatDateIt(dateFrom)} — {formatDateIt(dateTo)}</b></>
                )}
                {selectedEmps.length > 0 && (
                  <>
                    {" "}· Dipendenti selezionati:{" "}
                    <b>
                      {selectedEmps.length <= 3
                        ? selectedEmps
                            .map((id) => {
                              const e = employees.find((x) => x.id === id);
                              return e ? `${e.cognome} ${e.nome}` : "";
                            })
                            .filter(Boolean)
                            .join(", ")
                        : selectedEmps.length}
                    </b>
                  </>
                )}
              </div>
            </div>
            <div className="text-xs text-slate-500 text-right">
              Stampato il {formatDateIt(isoToday())}
            </div>
          </div>

          {/* Riepilogo totale finale */}
          {shownEmps.length > 0 && (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider">
                  Dipendenti
                </div>
                <div className="text-xl font-heading font-black">{totalAll.dip}</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider">
                  Giornate totali
                </div>
                <div className="text-xl font-heading font-black">{totalAll.gg}</div>
              </div>
              <div className="bg-[#0044FF]/5 border border-[#0044FF]/30 rounded p-3">
                <div className="text-xs text-[#0044FF] uppercase tracking-wider font-bold">
                  Ore totali
                </div>
                <div className="text-xl font-heading font-black text-[#0044FF]">
                  {totalAll.ore.toFixed(1)}h
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lista dipendenti */}
        {loading && !report ? (
          <div className="text-center py-12 text-slate-500">Caricamento…</div>
        ) : !shownEmps.length ? (
          <Card className="p-8 text-center text-slate-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <div className="font-bold mb-1">Nessun dato nel periodo selezionato</div>
            <div className="text-sm mb-4">
              Cambia le date o usa i pulsanti rapidi qui sotto:
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button variant="outline" size="sm" onClick={() => setQuickRange("prevMonth")}>
                Mese precedente
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange("last3Months")}>
                Ultimi 3 mesi
              </Button>
              <Button variant="outline" size="sm" onClick={() => setQuickRange("thisYear")}>
                Anno corrente
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {shownEmps.map((emp, idx) => (
              <Card
                key={emp.employee_id || emp.employee_nome}
                className={`overflow-hidden print:break-inside-avoid employee-print-page ${idx > 0 ? "print:mt-0" : ""}`}
                data-testid={`report-employee-${emp.employee_id || emp.employee_nome}`}
              >
                {/* Header dipendente */}
                <div className="bg-slate-800 text-white px-4 py-2.5 flex items-baseline justify-between print-emp-header">
                  <div className="font-heading font-bold text-base">
                    {emp.employee_nome}
                  </div>
                  <div className="text-sm">
                    <span className="opacity-70">{emp.tot_giorni} gg ·</span>{" "}
                    <b className="text-base">{emp.tot_ore}h</b>
                  </div>
                </div>

                {/* Sub-header periodo (visibile solo in stampa, per pagina autonoma) */}
                <div className="hidden print:block px-4 py-1.5 border-b border-slate-200 text-xs text-slate-600 bg-slate-50 print-subheader">
                  Periodo:{" "}
                  <b>
                    {sameMonth
                      ? monthLabelIt(dateFrom)
                      : `${formatDateIt(dateFrom)} — ${formatDateIt(dateTo)}`}
                  </b>{" "}
                  · Stampato il {formatDateIt(isoToday())}
                </div>

                {/* Tabella giorni */}
                <div className="px-3 pt-2 flex flex-wrap gap-2 text-[10px] font-semibold text-slate-600 print-legend">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-emerald-200 border border-emerald-300" />
                    Lavorato
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-slate-200 border border-slate-300" />
                    Non lavorato
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-sky-200 border border-sky-300" />
                    Sabato
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded bg-rose-200 border border-rose-300" />
                    Domenica / Festivo
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2">Data</th>
                        <th className="text-left px-3 py-2 hidden sm:table-cell">Commesse</th>
                        <th className="text-right px-3 py-2">Lavoro</th>
                        <th className="text-right px-3 py-2 hidden sm:table-cell">Caldo</th>
                        <th className="text-right px-3 py-2 hidden sm:table-cell">Notte</th>
                        <th className="text-right px-3 py-2 hidden sm:table-cell">Guida</th>
                        <th className="text-right px-3 py-2 hidden sm:table-cell">Pass.</th>
                        <th className="text-right px-3 py-2 bg-[#0044FF]/5">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emp.giorni.map((g) => (
                        <tr
                          key={g.data}
                          className={`border-t border-slate-200 hover:opacity-90 ${dayClass(g.data, g.ore_tot)}`}
                          data-testid={`report-row-${emp.employee_id}-${g.data}`}
                        >
                          <td className="px-3 py-1.5 font-bold">
                            {formatDateIt(g.data)}
                          </td>
                          <td className="px-3 py-1.5 text-slate-600 hidden sm:table-cell text-xs print:text-[8pt] print:px-1.5 print-commesse">
                            {g.commesse.join(", ") || "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {g.ore_lavoro || "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums hidden sm:table-cell text-amber-700">
                            {g.ore_caldo > 0 ? g.ore_caldo : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums hidden sm:table-cell text-indigo-700">
                            {g.ore_notturne > 0 ? g.ore_notturne : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums hidden sm:table-cell text-emerald-700">
                            {g.ore_guida > 0 ? g.ore_guida : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums hidden sm:table-cell text-slate-500">
                            {g.ore_passegg > 0 ? g.ore_passegg : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right font-bold tabular-nums bg-[#0044FF]/5 whitespace-nowrap">
                            {g.ore_tot}h
                            {g.ore_non_vidimate > 0 && (
                              <span
                                className="ml-1 text-[9px] font-semibold text-amber-700 align-middle"
                                title={`${g.ore_non_vidimate}h da rapportini non ancora vidimati`}
                                data-testid={`dv-${emp.employee_id || emp.employee_nome}-${g.data}`}
                              >
                                ({g.ore_non_vidimate < g.ore_tot ? `${g.ore_non_vidimate}h ` : ""}DV)
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {/* Totale finale del dipendente */}
                      <tr className="border-t-2 border-slate-800 bg-slate-100 font-bold">
                        <td className="px-3 py-2">
                          TOTALE ({emp.tot_giorni} gg)
                        </td>
                        <td className="px-3 py-2 hidden sm:table-cell"></td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {emp.tot_ore_lavoro}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell text-amber-700">
                          {emp.tot_ore_caldo || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell text-indigo-700">
                          {emp.tot_ore_notturne || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell text-emerald-700">
                          {emp.tot_ore_guida || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell text-slate-500">
                          {emp.tot_ore_passegg || "—"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums bg-[#0044FF] text-white text-base whitespace-nowrap">
                          {emp.tot_ore}h
                          {emp.tot_ore_non_vidimate > 0 && (
                            <span className="ml-1 text-[9px] font-semibold text-amber-200 align-middle" data-testid={`dv-tot-${emp.employee_id || emp.employee_nome}`}>
                              ({emp.tot_ore_non_vidimate}h DV)
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Stili print */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print-area { padding: 0; }
          @page { size: A4 portrait; margin: 0.8cm; }

          /* Riepilogo globale stampato solo sulla prima pagina */
          .print-summary { margin-bottom: 0.6cm; }

          /* Ogni dipendente su una pagina A4 verticale dedicata.
             Iter 31cc: ANCHE il primo dipendente parte su una pagina nuova,
             così il riepilogo globale resta da solo sulla prima pagina. */
          .employee-print-page {
            break-before: page;
            page-break-before: always;
            break-inside: avoid;
            page-break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #1f2937 !important;
          }

          /* Compatta tipografia per far stare un mese INTERO (31 giorni +
             riga TOTALE) in UNA sola facciata A4. Iter 31ee: prima con
             font 11pt e padding 3px la tabella sforava di poche righe e la
             riga TOTALE finiva da sola su pagina 2. */
          .employee-print-page table {
            font-size: 9.5pt;
            line-height: 1.25;
          }
          .employee-print-page thead th {
            padding: 3px 6px;
            font-size: 8pt;
          }
          .employee-print-page tbody td { padding: 2px 6px; }
          /* Riga TOTALE: compatta anche lei (py-2 a video = troppo in stampa) */
          .employee-print-page tbody tr:last-child td { padding: 3px 6px; }
          /* Header dipendente + sub-header periodo + legenda: più compatti */
          .print-emp-header { padding: 4px 12px !important; }
          .print-emp-header .text-base { font-size: 11pt !important; }
          .print-subheader { padding: 2px 12px !important; font-size: 8pt !important; }
          .print-legend {
            padding-top: 2px !important;
            padding-bottom: 2px !important;
            font-size: 7pt !important;
          }
          .print-legend .w-3 { width: 8px !important; height: 8px !important; }
          /* Iter 31cc: codici commessa più stretti in stampa */
          .print-commesse {
            font-size: 7pt !important;
            letter-spacing: -0.2px;
            line-height: 1.1;
            word-break: break-all;
            max-width: 30%;
          }

          /* In stampa mostro anche colonne nascoste su mobile */
          .hidden.sm\\:table-cell { display: table-cell !important; }
        }
      `}</style>
    </div>
  );
}
